import { axiosClient, unauthenticatedAxiosClient } from '@App/api/api-client'
import { AxiosRequestConfig, AxiosResponse } from 'axios'
import Pathes from '@App/dicts/pathes'
import { FetchType } from '@Components/WorkItemV2/fetch-type-enum'
import { PaginationBody } from '@Entities/pagination'
import { HistoryWorkQuery } from '@Entities/search'
import { WorkModel } from '@Entities/work/work'
import { ClaimFilterValues } from '@Components/ClaimComponents/claim-filter'
import { WriterModel } from '@Entities/work/writer-model'
import { Works } from '@Entities/work/works'
import { WorksSearchModel } from '@Entities/work/works-search-model'
import { WorksFromUpload } from '@Entities/work/works-from-upload'

export const getUnclaimedWorks = (
    pagination: PaginationBody,
    search?: HistoryWorkQuery,
    filter?: ClaimFilterValues
): Promise<AxiosResponse<WorksSearchModel>> => {
    const request = { ...search, ...filter }
    return axiosClient.post<WorksSearchModel>(Pathes.RightsHolder.unclaimedSearch(pagination), request)
}

export const postWork = (
    data: WorkModel,
    rightsholderId: number,
    originalSubmissionId?: number
): Promise<AxiosResponse<WorkModel>> =>
    axiosClient.post<WorkModel>(Pathes.Work.postWork(rightsholderId, originalSubmissionId), data)

export const getWorkById = (
    id: number,
    type: FetchType,
    rightsholderId: number
): Promise<AxiosResponse<WorkModel>> | null => {
    switch (type) {
        case FetchType.TRANSACTION:
            return axiosClient.get<WorkModel>(Pathes.Work.submissionById(id, rightsholderId))
        case FetchType.HFA:
            return axiosClient.get<WorkModel>(Pathes.Work.summaryById(id, rightsholderId))
        case FetchType.MLC:
            return axiosClient.get<WorkModel>(Pathes.Work.workById(id, rightsholderId))
    }
}

export const getWorkCatalogById = (id: string): Promise<AxiosResponse<WorkModel>> =>
    unauthenticatedAxiosClient.get<WorkModel>(Pathes.Work.catalogWorkById(id))

export const saveUploadWorks = (
    rightsHolderId: number,
    works: WorksFromUpload
): Promise<AxiosResponse<WorksFromUpload>> =>
    axiosClient.post<WorksFromUpload>(Pathes.Upload.works(rightsHolderId), works)

export const getCompleteWorksFromUpload = (
    rightsholderId: number,
    fileId: number,
    pagination: PaginationBody
): Promise<AxiosResponse<WorksFromUpload>> => {
    const request: AxiosRequestConfig = {
        data: pagination,
    }
    return axiosClient.get<WorksFromUpload>(Pathes.Upload.works(rightsholderId, fileId), request)
}

export const getCurrentWorksFromUploadFile = (
    rightsHolderId: number,
    pagination: PaginationBody,
    data: FormData
): Promise<AxiosResponse<WorksFromUpload>> =>
    axiosClient.post<WorksFromUpload>(Pathes.Upload.files(pagination, rightsHolderId), data, {
        transformRequest: [
            (transformData, headers) => {
                if (transformData) {
                    headers['Content-Type'] = 'multipart/form-data'
                }
                return data
            },
        ],
    })

export const worksDraftsRH = (
    rightsHolderId: number,
    pagination?: PaginationBody | undefined
): Promise<AxiosResponse<Works>> => axiosClient.get<Works>(Pathes.Work.worksDraftsRH(rightsHolderId, pagination))

export const deleteAllDraftsRightsHolder = (rightsHolderId: number): Promise<AxiosResponse<Works>> =>
    axiosClient.delete<Works>(Pathes.Work.worksDraftsRH(rightsHolderId))

export const deleteDraftRightsHolder = (draftId: number): Promise<AxiosResponse<Works>> =>
    axiosClient.delete<Works>(Pathes.Work.workDraftRH(draftId))

export const createWriter = (writer: WriterModel): Promise<AxiosResponse<WriterModel>> =>
    axiosClient.post<WriterModel>(Pathes.Work.createWriter, writer)

export const searchSuggestions = (
    property: string,
    searchTerm: string,
    notClaimedBy: string | undefined,
    isOverclaimable: boolean | undefined,
    signal: AbortSignal
): Promise<AxiosResponse<string[]>> =>
    unauthenticatedAxiosClient.get<string[]>(
        Pathes.Search.searchSuggestions(property, searchTerm, notClaimedBy, isOverclaimable),
        { signal }
    )

export const dismissWork = (rightsholderId: number, workId: number) =>
    axiosClient.patch(Pathes.Work.dismissWork(rightsholderId, workId))
